import { useEffect, useState } from 'react';
import type { NoticeView } from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';

const ago = (iso: string) => {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// The bell: what happened while you were busy (message requests for now). Opening it marks them read.
export function NoticesPanel({
  onClose,
  onRead,
  onOpenChat,
}: {
  onClose: () => void;
  onRead: () => void;
  onOpenChat: (lifeId: string) => void;
}) {
  const [notices, setNotices] = useState<NoticeView[] | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  useEffect(() => {
    let alive = true;
    void api
      .notices()
      .then((reply) => {
        if (!alive) return;
        setNotices(reply.notices);
        if (reply.unread) void api.readNotices().then(onRead, () => {});
      })
      .catch((error: unknown) => alive && setFailure(describeError(error)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="notices-layer" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <section className="notices-panel" role="dialog" aria-modal="true" aria-label="Notifications">
        <header>
          <h2>Notifications</h2>
          <button type="button" aria-label="Close notifications" onClick={onClose}>
            ×
          </button>
        </header>
        {failure && <ErrorNotice error={failure} />}
        {notices && !notices.length && <p className="chat-empty">Nothing new. Messages and news land here.</p>}
        <ul>
          {notices?.map((notice) => (
            <li key={notice.id}>
              <button
                type="button"
                className={notice.read ? 'notice' : 'notice unread'}
                disabled={!notice.link}
                onClick={() => notice.link && onOpenChat(notice.link.lifeId)}
              >
                <span className="notice-icon" aria-hidden="true">
                  {notice.kind.startsWith('advert_') ? '📢' : '📨'}
                </span>
                <span className="notice-text">
                  <b>{notice.title}</b>
                  {notice.body && <small>{notice.body}</small>}
                </span>
                <time>{ago(notice.createdAt)}</time>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
